/**
 * CapabilityRoute: wraps a route element with a per-tenant capability check
 * against `Ecosystem.capabilities[name]`. If the active tenant has disabled
 * the named capability, the route redirects to `/`; otherwise children
 * render as-is.
 *
 * Composition pattern: the capability check is orthogonal to authentication.
 * Wrap with the appropriate auth/role guard INSIDE the capability boundary,
 * e.g.:
 *
 *   <CapabilityRoute name="opportunities">
 *       <ProtectedRoutes>
 *           <OpportunityForm mode="create" />
 *       </ProtectedRoutes>
 *   </CapabilityRoute>
 *
 * Why redirect to `/` rather than show "Feature not enabled": this mirrors
 * EcosystemAdminRoute's apex-mode redirect: the user shouldn't see a
 * dead-end page for a feature their tenant doesn't run. The home page is
 * always meaningful; "feature disabled" is a no-op surface.
 *
 * Platform-mode pass-through: `useCapability` returns `true` on the apex,
 * so this guard is a no-op there. That matches the design intent:
 * capability flags are a per-tenant concept.
 *
 * Like EcosystemAdminRoute, this client gate is a UX affordance. The server
 * enforces the real boundary via `requireCapability(name)` middleware
 * mounted on the corresponding API routes: a non-tenant request that
 * bypasses the client gate still 404s from the API.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

import useCapability from '@/shared/hooks/useCapability';

export default function CapabilityRoute({ name, children }) {
    const enabled = useCapability(name);

    if (!enabled) {
        return <Navigate to="/" replace />;
    }

    return children;
}

CapabilityRoute.propTypes = {
    name: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};
